import React, { useState } from 'react';
import usaFlagIcon from '../assets/usa-flag-icon.webp';
import indiaFlagIcon from '../assets/india-flag-icon.webp';
import LinkedinLogo from '../assets/LinkedIn-logo-blue.webp';

const ContactUs = () => {
    const [formData, setFormData] = useState({
        fullName: '',
        email: '',
        message: ''
    });

    // Submission states
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
    const [errorMessage, setErrorMessage] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Reset previous status
        setSubmitStatus(null);
        setErrorMessage('');

        // Validate required fields (fullName and email are required, message is optional)
        if (!formData.fullName.trim()) {
            setSubmitStatus('error');
            setErrorMessage('Please enter your full name.');
            return;
        }

        if (!formData.email.trim()) {
            setSubmitStatus('error');
            setErrorMessage('Please enter your email address.');
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setSubmitStatus('error');
            setErrorMessage('Please enter a valid email address.');
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await fetch('https://eiurbnj5z5.execute-api.us-east-1.amazonaws.com/contact-us-submit', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    fullName: formData.fullName.trim(),
                    email: formData.email.trim(),
                    message: formData.message.trim() || undefined
                })
            });

            if (response.ok) {
                setSubmitStatus('success');
                // Reset form on success
                setFormData({ fullName: '', email: '', message: '' });
            } else {
                const errorData = await response.text();
                setSubmitStatus('error');
                setErrorMessage(errorData || 'Something went wrong. Please try again.');
            }
        } catch (error) {
            setSubmitStatus('error');
            setErrorMessage('Network error. Please check your connection and try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Reset form to submit another message
    const handleReset = () => {
        setSubmitStatus(null);
        setErrorMessage('');
    };

    // Email icon
    const EmailIcon = () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="4" width="20" height="16" rx="2" />
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
        </svg>
    );

    // Phone icon
    const PhoneIcon = () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
    );

    // Location icon
    const LocationIcon = () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="10" r="3" />
            <path d="M12 2a8 8 0 0 0-8 8c0 1.892.402 3.13 1.5 4.5L12 22l6.5-7.5c1.098-1.37 1.5-2.608 1.5-4.5a8 8 0 0 0-8-8z" />
        </svg>
    );

    return (
        <section
            id="contact-us"
            className="w-full py-[60px] md:py-20"
            style={{ backgroundColor: '#F1F1F1', minHeight: '622px' }}
        >
            {/* Container div1 */}
            <div className="site-shell flex flex-col lg:flex-row gap-[40px] lg:gap-[80px] lg:justify-between items-stretch">

                {/* Left Side - div1.1 (Title + Form) */}
                <div className="w-full lg:w-[500px] flex flex-col gap-[10px]">
                    {/* Title Section */}
                    <div className="flex flex-col gap-[8px] mb-[10px]">
                        <h2 className="font-oswald text-center md:text-left font-bold text-[40px] md:text-[56px] leading-none uppercase text-black">
                            CONTACT US
                        </h2>
                        <p className="font-roboto text-center md:text-left font-normal text-[16px] md:text-[18px] leading-[1.5] text-gray-700">
                            We'd love to hear from you.
                        </p>
                    </div>

                    {/* Success Message */}
                    {submitStatus === 'success' ? (
                        <div className="bg-white p-6 rounded-[6px] flex flex-col items-center gap-4 border border-gray-200">
                            {/* Success Icon */}
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M20 6L9 17L4 12" stroke="#22C55E" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>

                            <h3 className="font-inter font-bold text-xl text-[#090E1B] text-center">
                                Thank You!
                            </h3>

                            <p className="font-inter text-gray-600 text-center text-sm leading-relaxed">
                                Your message has been sent successfully. Our team will get back to you shortly.
                            </p>

                            <button
                                onClick={handleReset}
                                className="mt-2 px-6 py-2 bg-[#00D4FF] text-black font-inter font-semibold text-[14px] rounded-[6px] hover:bg-[#00b8e0] transition-colors"
                            >
                                Send Another Message
                            </button>
                        </div>
                    ) : (
                        /* Form */
                        <form onSubmit={handleSubmit} className="flex flex-col gap-[16px]">
                            {/* Error Message Banner */}
                            {submitStatus === 'error' && (
                                <div className="w-full bg-red-50 border border-red-200 rounded-[6px] p-3 flex items-start gap-3">
                                    <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                    <p className="font-inter text-red-700 text-sm">{errorMessage}</p>
                                </div>
                            )}

                            {/* Full Name Field */}
                            <div className="flex flex-col gap-[6px]">
                                <label
                                    htmlFor="fullName"
                                    className="font-inter font-medium text-[13px] text-gray-800"
                                >
                                    Full Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="fullName"
                                    name="fullName"
                                    value={formData.fullName}
                                    onChange={handleChange}
                                    placeholder="Full Name"
                                    disabled={isSubmitting}
                                    className="w-full h-[44px] px-4 bg-white border border-gray-300 rounded-[6px] font-inter text-[14px] text-gray-600 placeholder-gray-400 focus:outline-none focus:border-[#00D4FF] transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                                />
                            </div>

                            {/* Email Field */}
                            <div className="flex flex-col gap-[6px]">
                                <label
                                    htmlFor="email"
                                    className="font-inter font-medium text-[13px] text-gray-800"
                                >
                                    Email <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="you@company.com"
                                    disabled={isSubmitting}
                                    className="w-full h-[44px] px-4 bg-white border border-gray-300 rounded-[6px] font-inter text-[14px] text-gray-600 placeholder-gray-400 focus:outline-none focus:border-[#00D4FF] transition-colors disabled:bg-gray-100 disabled:cursor-not-allowed"
                                />
                            </div>

                            {/* Message Field */}
                            <div className="flex flex-col gap-[6px]">
                                <label
                                    htmlFor="message"
                                    className="font-inter font-medium text-[13px] text-gray-800"
                                >
                                    Message <span className="text-gray-400 font-normal">(optional)</span>
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    placeholder="Leave us a message..."
                                    rows={4}
                                    disabled={isSubmitting}
                                    className="w-full px-4 py-3 bg-white border border-gray-300 rounded-[6px] font-inter text-[14px] text-gray-600 placeholder-gray-400 focus:outline-none focus:border-[#00D4FF] transition-colors resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                                />
                            </div>

                            {/* Send Message Button */}
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`w-full h-[44px] bg-[#00D4FF] text-white font-inter font-semibold text-[15px] rounded-[6px] transition-colors flex items-center justify-center gap-2 ${isSubmitting
                                    ? 'opacity-70 cursor-not-allowed'
                                    : 'hover:bg-[#00b8e0] cursor-pointer'
                                    }`}
                            >
                                {isSubmitting ? (
                                    <>
                                        <svg className="animate-spin h-5 w-5 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Sending...
                                    </>
                                ) : (
                                    'Send message'
                                )}
                            </button>
                        </form>
                    )}

                    {/* LinkedIn Button */}
                    <a
                        href="https://www.linkedin.com/company/fintechglobalcenter/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-full h-[44px] flex items-center justify-center gap-[10px] bg-white border border-gray-300 rounded-[6px] font-inter font-medium text-[15px] text-black hover:bg-gray-50 transition-colors"
                    >
                        <img src={LinkedinLogo} alt="LinkedIn" className="h-5 w-5" />
                        Follow us on LinkedIn
                    </a>
                </div>

                {/* Right Side - div1.2 (USA and INDIA Addresses) */}
                <div className="w-full lg:w-[450px] flex flex-col gap-[30px] lg:pt-[80px] justify-end">

                    {/* USA Section */}
                    <div className="flex flex-col gap-[16px]">
                        <div className="flex items-center gap-[12px]">
                            {/* Replace your actual flag/logo component or img here */}
                            <img src={usaFlagIcon} alt="USA Flag" className="w-6 h-auto" />

                            <h3 className="font-inter font-bold text-[20px] text-black">USA</h3>
                        </div>
                        <div className="flex flex-col gap-[12px]">
                            {/* Email */}
                            <div className="flex items-center gap-[12px]">
                                <span className="text-gray-600"><EmailIcon /></span>
                                <a
                                    href="mailto:contact@fintechglobal.center"
                                    className="font-inter text-[15px] text-gray-700 hover:text-[#00D4FF] transition-colors"
                                >
                                    tmsdemo@fintechglobal.center
                                </a>
                            </div>

                            {/* Phone */}
                            <div className="flex items-center gap-[12px]">
                                <span className="text-gray-600"><PhoneIcon /></span>
                                <a
                                    href="tel:+16466887500"
                                    className="font-inter text-[15px] text-gray-700 hover:text-[#00D4FF] transition-colors"
                                >
                                    +1-646-688-7500
                                </a>
                            </div>

                            {/* Address */}
                            <div className="flex items-start gap-[12px]">
                                <span className="text-gray-600 mt-[2px]"><LocationIcon /></span>
                                <p className="font-inter text-[15px] text-gray-700 leading-[1.6]">
                                    101 Crawfords Corner Rd, Suite 4116<br />
                                    Holmdel, NJ 07733
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="h-[1px] bg-gray-300 w-full"></div>

                    {/* INDIA Section */}
                    <div className="flex flex-col gap-[16px]">
                        <div className="flex items-center gap-[12px]">
                            {/* Replace your actual flag/logo component or img here */}
                            <img src={indiaFlagIcon} alt="India Flag" className="w-6 h-auto" />

                            <h3 className="font-inter font-bold text-[20px] text-black">India</h3>
                        </div>

                        <div className="flex flex-col gap-[12px]">
                            {/* Email */}
                            <div className="flex items-center gap-[12px]">
                                <span className="text-gray-600"><EmailIcon /></span>
                                <a
                                    href="mailto:contact@fintechglobal.center"
                                    className="font-inter text-[15px] text-gray-700 hover:text-[#00D4FF] transition-colors"
                                >
                                    tmsdemo@fintechglobal.center
                                </a>
                            </div>

                            {/* Phone */}
                            <div className="flex items-center gap-[12px]">
                                <span className="text-gray-600"><PhoneIcon /></span>
                                <a
                                    href="tel:+918128287500"
                                    className="font-inter text-[15px] text-gray-700 hover:text-[#00D4FF] transition-colors"
                                >
                                    +91-812-828-7500
                                </a>
                            </div>

                            {/* Address */}
                            <div className="flex items-start gap-[12px]">
                                <span className="text-gray-600 mt-[2px]"><LocationIcon /></span>
                                <p className="font-inter text-[15px] text-gray-700 leading-[1.6]">
                                    18th Floor, GIFT Two Tower,<br />
                                    Gujarat International Finance Tec-City,<br />
                                    Gandhinagar, Gujarat 382355
                                </p>
                            </div>
                            {/* , Gujarat International Finance Tec-City, Gandhinagar, Gujarat  */}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ContactUs;
